import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {AppConfigService} from './app-config.service';
import {PagerStateModel} from '../DataModels/pager-state-model';
import {Observable} from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PagerStateService {

  constructor(private router: Router,
              private appConfig: AppConfigService) {}

  storePagerState(pageUrl: string, pagerState: PagerStateModel) {
    // console.log('storePagerState with these params: ' + pageUrl.split('?')[0], JSON.stringify(pagerState));
    const keyOnly = pageUrl.split('?')[0];
    localStorage.setItem('pager_state_' + keyOnly, JSON.stringify(pagerState));
    // Bridge legacy API to the new session/in-memory cache so old callers get the new behavior too
    try {
      const key = this.buildKey(keyOnly);
      this.storeState(key, pagerState);
    } catch {
      // ignore bridging errors
    }
  }

  getPagerStateObservable(pageUrl: string): Observable<PagerStateModel> {
    const keyOnly = pageUrl.split('?')[0];
    const storageKey = 'pager_state_' + keyOnly;
    const savedState = localStorage.getItem(storageKey);

    return new Observable<PagerStateModel>((observer) => {
      // First, try to restore from the new per-tab cache (in-memory/sessionStorage) with TTL
      try {
        const key = this.buildKey(keyOnly);
        const restored = this.restoreState(key);
        if (restored) {
          observer.next(restored);
          observer.complete();
          return;
        }
      } catch {
        // ignore and fall back to legacy localStorage path
      }

      if (savedState) {
        const now = Date.now();
        const pagerState: PagerStateModel = JSON.parse(savedState);
        if (now <= pagerState.dateTime + this.appConfig.pagerStateSaveTime) {
          // console.log('Observable Returning these pager settings: ' + JSON.stringify(pagerState));
          observer.next(pagerState);
          observer.complete();
          return;
        }
        // Clean up expired legacy entry to avoid stale buildup
        localStorage.removeItem(storageKey);
      }

      // nothing saved or is expired
      const expiredState = new PagerStateModel();
      expiredState.lastPage = 0;
      expiredState.lastPageSize = 0;
      expiredState.startIndex = 0;
      expiredState.endIndex = 0;
      expiredState.totalRecords = 0;
      expiredState.dateTime = 0;

      // no longer a valid key based on time lapse so just remove it (both legacy and new cache)
      localStorage.removeItem(storageKey);
      try {
        const key = this.buildKey(keyOnly);
        this.clearState(key);
      } catch {
        // ignore
      }

      // console.log('Observable Expired pager state, returning default: ' + JSON.stringify(expiredState));
      observer.next(expiredState);
      observer.complete();
    });
  }

  // --- Enhanced, backward-compatible helpers ---
  // In-memory + sessionStorage cache for per-tab, time-limited persistence
  // without changing existing localStorage-based API used by the app today.
  private readonly __PAGER_MEM = new Map<string, PagerStateModel>();
  private readonly __PAGER_STORAGE_PREFIX = 'pager_session:';

  // Build a stable key for list routes; optionally include extras like subLedger
  buildKey(url: string, extras?: Record<string, any>): string {
    try {
      const u = new URL(url, window.location.origin);
      const parts = u.pathname.split('/').filter(Boolean);
      // keep the first 5 segments for stability (e.g., transactions/invoice/list/ar)
      const basePath = parts.slice(0, 5).join('/');
      const qp = new URLSearchParams();
      if (extras && extras['subLedger']) {
        qp.set('subLedger', String(extras['subLedger']));
      }
      if (parts.length >= 1) {
        qp.set('type', parts[parts.length - 1]);
      }
      return `/${basePath}?${qp.toString()}`;
    } catch {
      return url;
    }
  }

  storeState(key: string, state: PagerStateModel): void {
    const snap: PagerStateModel = {...state, dateTime: Date.now()};
    this.__PAGER_MEM.set(key, snap);
    try {
      sessionStorage.setItem(this.__PAGER_STORAGE_PREFIX + key, JSON.stringify(snap));
    } catch {
    }
  }

  restoreState(key: string): PagerStateModel | undefined {
    const now = Date.now();
    const ttl = Number(this.appConfig?.pagerStateSaveTime) || (1000 * 60 * 60); // fall back to 1h
    const mem = this.__PAGER_MEM.get(key);
    if (mem && now - (mem.dateTime || 0) < ttl) {
      return this.sanitize(mem);
    }
    try {
      const raw = sessionStorage.getItem(this.__PAGER_STORAGE_PREFIX + key);
      if (raw) {
        const parsed = JSON.parse(raw) as PagerStateModel;
        if (parsed && now - (parsed.dateTime || 0) < ttl) {
          this.__PAGER_MEM.set(key, parsed);
          return this.sanitize(parsed);
        } else {
          sessionStorage.removeItem(this.__PAGER_STORAGE_PREFIX + key);
          return undefined;
        }
      }
    } catch {
    }
    return undefined;
  }

  clearState(key: string): void {
    this.__PAGER_MEM.delete(key);
    try {
      sessionStorage.removeItem(this.__PAGER_STORAGE_PREFIX + key);
    } catch {
    }
  }

  private sanitize(s: PagerStateModel): PagerStateModel {
    const page = Math.max(1, Number(s.lastPage) || 1);
    const size = Math.max(1, Number(s.lastPageSize) || 10);
    const total = Math.max(0, Number(s.totalRecords) || 0);
    const start = Math.max(0, Number(s.startIndex) || 0);
    const end = Math.max(start, Number(s.endIndex) || 0);
    return {lastPage: page, lastPageSize: size, totalRecords: total, startIndex: start, endIndex: end, dateTime: s.dateTime};
  }
}
